const UriLib = require('./UriLib');
const { wildcardToRegex } = require('./wildcard');

// Ported byte-for-byte from private-cache's `findUrlParameter`
// (services/private-cache/middleware/ignore-url-parameters-whatwg.ts) so the
// live bot-request path, bulk reconciliation, and recache self-heal share one
// matcher and cannot fork. `entries` are url_parameters rows shaped `{ value }`.
function matchesQueryParamEntry(entries, parameterName) {
  return entries.some((entry) => {
    // A LEFT JOIN aggregate hands whitelist-enabled users with zero
    // url_parameters rows a [{ value: null, ignore: null }] stub — never match
    // on it (and never crash on it).
    if (typeof entry.value !== 'string') {
      return false;
    }
    if (entry.value.includes('*')) {
      return wildcardToRegex(entry.value).test(parameterName);
    }
    if (entry.value === parameterName) {
      return true;
    }
    const unencodedValue = UriLib.decodeURIComponent(entry.value);
    if (unencodedValue === parameterName) {
      return true;
    }
    return false;
  });
}

function moveQueryStringAfterHashToQuery(parsedUrl) {
  const hasQueryStringAfterHash = parsedUrl.search.length === 0 && parsedUrl.hash.indexOf('?') !== -1;
  if (hasQueryStringAfterHash) {
    parsedUrl.search = parsedUrl.hash.substr(parsedUrl.hash.indexOf('?') + 1);
  }
  return { hasQueryStringAfterHash, parsedUrl };
}

function restoreHash(parsedUrl) {
  parsedUrl.hash = parsedUrl.hash.substr(0, parsedUrl.hash.indexOf('?'));
  if (parsedUrl.search.length > 0) {
    parsedUrl.hash += `?${parsedUrl.search}`;
  }
  parsedUrl.search = '';
  return parsedUrl;
}

// Ported byte-for-byte from private-cache's `ignoreQueryParamWhiteList`. Strips
// every query param not matched by the whitelist, preserving the hash-query
// move/restore behavior exactly. `changed` is true iff at least one param was
// stripped.
function normalizeUrlForWhitelist(url, whitelistEntries) {
  // eslint-disable-next-line prefer-const
  let { hasQueryStringAfterHash, parsedUrl } = moveQueryStringAfterHashToQuery(new URL(url));
  const toDeleteParams = [];
  parsedUrl.searchParams.forEach((_value, name) => {
    if (!matchesQueryParamEntry(whitelistEntries, name)) {
      toDeleteParams.push(name);
    }
  });

  toDeleteParams.forEach((paramName) => {
    parsedUrl.searchParams.delete(paramName);
  });
  if (hasQueryStringAfterHash) {
    parsedUrl = restoreHash(parsedUrl);
  }

  return { url: parsedUrl.toString(), changed: toDeleteParams.length > 0 };
}

// An enabled whitelist carrying zero usable entries is a data anomaly, not an
// instruction to strip every query parameter. Both the bulk reconciliation
// (queue-service) and the recache self-heal (recache-server) skip on this so a
// misconfigured account is never mass-normalized; the single definition here
// keeps the two paths from drifting. Entries without a string value count as
// absent: a LEFT JOIN aggregate represents "no url_parameters rows" as
// [{ value: null, ignore: null }], not [].
function isEmptyWhitelistAnomaly(whitelistEntries) {
  if (!whitelistEntries) {
    return true;
  }
  return !whitelistEntries.some((entry) => entry && typeof entry.value === 'string');
}

module.exports = { matchesQueryParamEntry, normalizeUrlForWhitelist, isEmptyWhitelistAnomaly };
